import React, { useState, useEffect , useContext } from 'react';
import displayError from '../../utils/ErrorHandler';
import axios from 'axios';
import CommitModal from './CommitModal'; // Import the modal component
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';
import CreateSilatItemModal from './CreateSilatItemModal';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { isAdminRole } from '../../utils/roles';
import { n2 } from '../../utils/fmt';

const ListItems = () => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [commitType, setCommitType] = useState('item');
  const {user,setUser} = useContext(UserContext);

  const [isAddSilatItemModal, setIsAddSilatItemModal] = useState(false);
  
  // Store commitments data for Excel export
  const [commitmentsData, setCommitmentsData] = useState([]);
  
  const navigate = useNavigate();

  // Auth check
  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, setUser, navigate]);

  const loadData = async () => {
  setLoading(true);
  try {
    const [itemRes, commitRes] = await Promise.all([
      axios.get(`${process.env.REACT_APP_API_URI}/api/silatItem/getAll`, { withCredentials: true }),
      axios.get(`${process.env.REACT_APP_API_URI}/api/commit/getAll`, { withCredentials: true })
    ]);

    const itemsData = itemRes.data.data;
    const commitData = commitRes.data.data;

    // Store commitments data for Excel export
    setCommitmentsData(commitData);

    // Map commits by itemId for quick lookup
    const commitMap = {};
    commitData.forEach(commit => {
      if (!commitMap[commit.silatItem._id]) {
        commitMap[commit.silatItem._id] = 0;
      }
      commitMap[commit.silatItem._id] += commit.quantity || 0;
    });

    const updatedItems = itemsData.map(item => {
      const committedQty = commitMap[item._id] || 0;
      const pendingQty = Math.floor(Math.max(item.requiredQuantity - committedQty, 0) * 10) / 10;

      return {
        ...item,
        pendingQuantity: pendingQty
      };
    });

    // Sort items alphabetically by name (or whatever field you want)
    const sortedItems = updatedItems.sort((a, b) => {
      // Assuming the field is 'name' - adjust to your actual field
      return a.name.localeCompare(b.name);
    });

    console.log(sortedItems);
    setItems(sortedItems);
  } catch (err) {
    const errorMessage = displayError(err);
    if (errorMessage) setMessage(errorMessage);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCommit = (itemId) => {
    const item = items.find(item => item._id === itemId);
    if (item) {
      setSelectedItem(item);
      setCommitType('item');
      setIsModalOpen(true);
    }
  };

  const handleCommitZabihat = () => {
    // Create a virtual zabihat item object
    const zabihatItem = {
      _id: 'zabihat',
      name: 'Zabihat',
      price: 150, // Set your default price here, or fetch from backend
      unit: 'Kg',
      requiredQuantity: 999999, // Large number to allow any quantity
      pendingQuantity: 999999,
      isZabihat: true
    };
    
    setSelectedItem(zabihatItem);
    setCommitType('zabihat');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setCommitType('item');
    loadData();
  };

  // Function to export data to Excel
  const handleExportToExcel = () => {
    try {
      // Create jamiat commitments map
      const jamiatCommitmentMap = {};
      
      commitmentsData.forEach(commit => {
        const itemId = commit.silatItem._id;
        const jamiatName = commit.jamiat?.jamiat_name || 'Unknown Jamiat';
        const jamaatName = commit.jamiat?.jamaat_name || 'Unknown Jamaat';
        const jamiatDisplay = `${jamiatName} - ${jamaatName}`;
        const quantity = commit.quantity || 0;

        if (!jamiatCommitmentMap[itemId]) {
          jamiatCommitmentMap[itemId] = [];
        }

        // Check if this jamiat already exists for this item
        const existingJamiat = jamiatCommitmentMap[itemId].find(j => j.name === jamiatDisplay);
        if (existingJamiat) {
          existingJamiat.quantity += quantity;
        } else {
          jamiatCommitmentMap[itemId].push({
            name: jamiatDisplay,
            quantity: quantity
          });
        }
      });

      // Prepare data for Excel
      const excelData = items.map(item => {
        // Get jamiat commitments for this item
        const jamiatCommitments = jamiatCommitmentMap[item._id] || [];
        const jamiatDetails = jamiatCommitments
          .map(j => `${j.name} - ${n2(j.quantity)}`)
          .join(', ');

        return {
          'Item Name': item.name,
          'Unit': item.unit,
          'Price (₹)': n2(item.price),
          'Required Quantity': n2(item.requiredQuantity),
          'Pending Quantity': n2(item.pendingQuantity),
          'Committed By (Jamiat - Quantity)': jamiatDetails || 'No commitments'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // Item Name
        { wch: 8 },  // Unit
        { wch: 12 }, // Price
        { wch: 15 }, // Required Quantity
        { wch: 15 }, // Pending Quantity
        { wch: 50 }  // Committed By (wider for jamiat details)
      ];
      ws['!cols'] = columnWidths;

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Silat Items');

      // Generate Excel file buffer
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      // Create blob and save file
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Silat_Items_Export_${currentDate}.xlsx`;
      
      saveAs(data, fileName);

      setMessage('Excel file exported successfully!');
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setMessage('Error exporting to Excel. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {message && (
        <div className={`text-center mt-4 p-2 rounded ${
          message.includes('Error') ? 'text-red-500 bg-red-50' : 'text-green-500 bg-green-50'
        }`}>
          {message}
        </div>
      )}
      {loading ? (
        <div className='flex justify-center items-center h-[70vh] w-full'>
          <p>Loading Please Wait...</p>
        </div>
      ) : (
        <div className="p-4">
          {/* Search Bar */}
          <div className="mb-4">
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb"
              placeholder="Search items..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>

          <div className="flex justify-center gap-2 flex-wrap mb-6">
            {/* Add Silat Items Button */}
            {isAdminRole(user?.role) && (
              <button
                onClick={() => setIsAddSilatItemModal(true)}
                className="flex items-center gap-2 
                           bg-gradient-to-r from-blue-500 to-blue-700 
                           hover:from-blue-600 hover:to-blue-800 
                           text-white font-bold py-3 px-8 rounded-full 
                           shadow-lg transform transition duration-200 
                           hover:scale-105 focus:outline-none focus:ring-4 
                           focus:ring-blue-300"
              >
                <span className="text-xl">+</span>
                Add Silat Item
              </button>
            )}
          
            {/* Commit Zabihat Button */}
            <button
              onClick={handleCommitZabihat}
              className="bg-gradient-to-r from-green-600 to-green-700 
                         hover:from-green-700 hover:to-green-800 
                         text-white font-bold py-3 px-8 rounded-full 
                         shadow-lg transform transition duration-200 
                         hover:scale-105 focus:outline-none focus:ring-4 
                         focus:ring-green-300"
            >
              Commit Zabihat
            </button>

            {/* Export to Excel Button */}
            <button
              onClick={handleExportToExcel}
              disabled={items.length === 0}
              className="flex items-center gap-2 
                         bg-gradient-to-r from-orange-500 to-orange-600 
                         hover:from-orange-600 hover:to-orange-700 
                         disabled:from-gray-400 disabled:to-gray-500
                         disabled:cursor-not-allowed
                         text-white font-bold py-3 px-8 rounded-full 
                         shadow-lg transform transition duration-200 
                         hover:scale-105 focus:outline-none focus:ring-4 
                         focus:ring-orange-300 disabled:hover:scale-100"
            >
              <span className="text-lg">📊</span>
              Export to Excel
            </button>
          </div>

          {/* Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.length !== 0 ? (filteredItems.filter(item => item.pendingQuantity !== 0).map(item => (
              <div key={item._id} className="bg-white p-4 rounded-lg shadow-md border border-fmb-light flex justify-between">
                <div>
                  <div>
                    <h2 className="text-lg font-semibold text-fmb">{item.name}</h2>
                    <p className="text-sm text-gray-700 mt-2">
                      Required Qty: {n2(item.requiredQuantity)} {item.unit}
                    </p>
                    <p className="text-sm text-gray-700">
                      Pending Qty: {n2(item.pendingQuantity)} {item.unit}
                    </p>
                    <p className="text-sm text-gray-700">Price/Unit: ₹{n2(item.price)}</p>
                  </div>
                </div>
                <button
                  className="mt-4 px-4 py-2 bg-fmb text-white rounded-md hover:bg-fmb-dark transition duration-200 self-start"
                  onClick={() => handleCommit(item._id)}
                >
                  Commit
                </button>
              </div>
            ))) : (
              <div className='flex justify-center items-center h-[70vh] w-[95vw]'>
                <p>No Items Found...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commit Modal */}
      <CommitModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        item={selectedItem}
        commitType={commitType}
      />
      {/* Create Silat Item Modal */}
      <CreateSilatItemModal
        isOpen={isAddSilatItemModal}
        onClose={() => {
          setIsAddSilatItemModal(false);
          loadData();
        }}
      />
    </>
  );
};

export default ListItems;